import { useEffect, useState } from 'react';

import { useGetApiV1PersonSharedPeopleMultiple } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Hook para busca server-side de alunos por nome, com debounce de 1 s e mínimo de 3 caracteres.
 *
 * @param filter - Texto digitado pelo usuário no campo de busca
 * @returns Lista de alunos com estado de loading e sucesso da query
 *
 * @example
 * ```tsx
 * const { students, isLoading, isQuerySuccess } = useStudentSearchDropdown(studentSearch);
 * ```
 */
export function useStudentSearchDropdown(filter = '') {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1PersonSharedPeopleMultiple(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  return {
    students: unwrapPagedResponse(data),
    isLoading,
    isQuerySuccess: isSuccess,
  } as const;
}
